// Angular imports

import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http'; //Required by PrimeNg file upload control.
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';

// app imports

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
// ngx-formly imports

import { FormlyModule } from '@ngx-formly/core';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';

// dspx-core imports

import {
    AddressFieldType,
    AutoCompleteFieldType,
    ButtonFieldType,
    CalendarFieldType,
    CardSectionFieldType,
    CardSectionOverviewFieldType,
    CardSectionRepeatingFieldType,
    CardSetPageFieldType,
    CardSetPageInfoFieldType,
    CardSetPageListFieldType,
    CardSetPageOverviewFieldType,
    CardSetPageRepeatingFieldType,
    CardSetTabsFieldType,
    CardSetTabsFlatFieldType,
    ChartFieldType,
    CheckboxFieldType,
    D3ChartFieldType,
    DateFieldType,
    DateTimeFieldType,
    DisplayFieldType,
    DocumentListFieldType,
    DspxCoreModule,
    DspxFileUploadWrapper,
    DspxLabelWrapperFormField,
    DspxRadioWrapperFormField,
    DspxWrapperContainer,
    DspxWrapperFilter,
    DspxWrapperFormField,
    DspxWrapperTableCell,
    EditorFieldType,
    ExcelFileUploadType,
    FIELD_TYPE,
    FIELD_VALIDATOR,
    FIELD_WRAPPER,
    FileUploadFieldType,
    FilterFieldType,
    FormNavigationFieldType,
    FormSummaryFieldType,
    FormSummaryV3FieldType,
    HorizontalStepperFieldType,
    HtmlHeadingFieldType,
    ImageFieldType,
    InputFieldType,
    ListFieldType,
    MaskedInputFieldType,
    MessageListFieldType,
    MessagesModule,
    MultiTimeRangeFieldType,
    MultiselectFieldType,
    MultiselectPillFieldType,
    PBIEmbeddedFieldType,
    PageFieldType,
    PageRepeatingFieldType,
    ParagraphFieldType,
    PasswordFieldType,
    RadioFieldType,
    RelatedEntitySummaryFieldType,
    SearchBoxFieldType,
    SectionFieldType,
    SectionRepeatingFieldType,
    SelectButtonFieldType,
    SelectFieldType,
    SingleCheckboxFieldType,
    StepsFieldType,
    SwitchFieldType,
    TableFieldType,
    TabsFieldType,
    TextAreaFieldType,
    TimeFieldType,
    VerticalStepperFieldType,
    customValidator,
    dateGreaterThanOrEqualToValidator,
    dateGreaterThanValidator,
    dateLessThanOrEqualToValidator,
    dateLessThanValidator,
    dateValidator,
    differentToValidator,
    dspxRequiredValidator,
    eitherMandatoryValidator,
    numberGreaterThanValidator,
    numberLessThanValidator,
    sameAsValidator,
    timeValidator,
    urlValidator,
    verificationRequiredValidator,
    whitespaceValidator
} from 'dspx-core';
import { FormResponseComponent } from './components/form-response/form-response.component';
import { LandingComponent } from './components/landing/landing.component';
import { MultiFormCompatibleCardSetFormComponent } from './components/multi-form-compatible-card-set-form/multi-form-compatible-card-set-form.component';
import { PendingChangesGuard } from './guards/pending-changes.guard';
import { datagridRequiredValidator } from './validators/datagridRequired.validator';


@NgModule({
  declarations: [
    AppComponent,
    MultiFormCompatibleCardSetFormComponent,
    LandingComponent,
    FormResponseComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    DspxCoreModule,
    MessagesModule,
    CardModule,
    ButtonModule,
    AvatarModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormlyPrimeNGModule,
    HttpClientModule,
    FormlyModule.forRoot({
      wrappers: [
        { name: FIELD_WRAPPER.formField, component: DspxWrapperFormField },
        { name: FIELD_WRAPPER.container, component: DspxWrapperContainer },
        { name: FIELD_WRAPPER.radio, component: DspxRadioWrapperFormField },
        { name: FIELD_WRAPPER.filter, component: DspxWrapperFilter },
        { name: FIELD_WRAPPER.file, component: DspxFileUploadWrapper },
        { name: FIELD_WRAPPER.label, component: DspxLabelWrapperFormField },
        { name: FIELD_WRAPPER.tableCell, component: DspxWrapperTableCell },
      ],
      types: [
        { name: FIELD_TYPE.input, component: InputFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.checkbox, component: SingleCheckboxFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.radio, component: RadioFieldType, wrappers: [FIELD_WRAPPER.radio] },
        { name: FIELD_TYPE.select, component: SelectFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.multiCheckbox, component: CheckboxFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.switch, component: SwitchFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.button, component: ButtonFieldType },
        { name: FIELD_TYPE.date, component: DateFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.datetime, component: DateTimeFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.file, component: FileUploadFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.autoComplete, component: AutoCompleteFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.address, component: AddressFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.maskedInput, component: MaskedInputFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.textArea, component: TextAreaFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.multiSelect, component: MultiselectFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.multiSelectPill, component: MultiselectPillFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.selectButton, component: SelectButtonFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.paragraph, component: ParagraphFieldType },
        { name: FIELD_TYPE.password, component: PasswordFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.steps, component: StepsFieldType },
        { name: FIELD_TYPE.tabs, component: TabsFieldType },
        { name: FIELD_TYPE.page, component: PageFieldType },
        { name: FIELD_TYPE.pageRepeating, component: PageRepeatingFieldType },
        { name: FIELD_TYPE.section, component: SectionFieldType },
        { name: FIELD_TYPE.sectionRepeating, component: SectionRepeatingFieldType },
        { name: FIELD_TYPE.formNavigation, component: FormNavigationFieldType },
        { name: FIELD_TYPE.dataGrid, component: TableFieldType },
        { name: FIELD_TYPE.search, component: SearchBoxFieldType, wrappers: [FIELD_WRAPPER.container] },
        { name: FIELD_TYPE.summaryPage, component: PageFieldType, wrappers: [FIELD_WRAPPER.container] },
        { name: FIELD_TYPE.summary, component: FormSummaryV3FieldType },
        { name: FIELD_TYPE.time, component: TimeFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.powerBi, component: PBIEmbeddedFieldType, wrappers: [FIELD_WRAPPER.container] },
        { name: FIELD_TYPE.heading, component: HtmlHeadingFieldType },
        { name: FIELD_TYPE.filter, component: FilterFieldType },
        { name: FIELD_TYPE.editor, component: EditorFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.image, component: ImageFieldType },
        { name: FIELD_TYPE.excelFileUpload, component: ExcelFileUploadType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.display, component: DisplayFieldType },
        { name: FIELD_TYPE.list, component: ListFieldType },
        { name: FIELD_TYPE.messageList, component: MessageListFieldType },
        { name: FIELD_TYPE.documentList, component: DocumentListFieldType },
        { name: FIELD_TYPE.calendar, component: CalendarFieldType, wrappers: [FIELD_WRAPPER.container] },
        { name: FIELD_TYPE.d3Chart, component: D3ChartFieldType, wrappers: [FIELD_WRAPPER.container] },
        { name: FIELD_TYPE.multiTimeRange, component: MultiTimeRangeFieldType, wrappers: [FIELD_WRAPPER.formField] },
        { name: FIELD_TYPE.chart, component: ChartFieldType },
        { name: FIELD_TYPE.verticalStepper, component: VerticalStepperFieldType },
        { name: FIELD_TYPE.horizontalStepper, component: HorizontalStepperFieldType },
        { name: 'related-entity-summary', component: RelatedEntitySummaryFieldType },
        // 'card-set' layout
        { name: FIELD_TYPE.cardsetTabs, component: CardSetTabsFieldType },
        { name: FIELD_TYPE.cardsetTabsFlat, component: CardSetTabsFlatFieldType },
        { name: FIELD_TYPE.cardsetPage, component: CardSetPageFieldType },
        { name: FIELD_TYPE.cardsetPageRepeating, component: CardSetPageRepeatingFieldType },
        { name: FIELD_TYPE.cardsetPageOverview, component: CardSetPageOverviewFieldType },
        { name: FIELD_TYPE.cardsetPageInfo, component: CardSetPageInfoFieldType },
        { name: FIELD_TYPE.cardsetPageList, component: CardSetPageListFieldType },
        { name: FIELD_TYPE.cardsetSection, component: CardSectionFieldType },
        { name: FIELD_TYPE.cardsetSectionRepeating, component: CardSectionRepeatingFieldType },
        { name: FIELD_TYPE.cardsetSectionOverview, component: CardSectionOverviewFieldType },
      ],
      validators: [
        { name: FIELD_VALIDATOR.required, validation: dspxRequiredValidator },
        { name: FIELD_VALIDATOR.whitespace, validation: whitespaceValidator },
        { name: FIELD_VALIDATOR.sameAs, validation: sameAsValidator },
        { name: FIELD_VALIDATOR.differentTo, validation: differentToValidator },
        { name: FIELD_VALIDATOR.eitherMandatory, validation: eitherMandatoryValidator },
        { name: FIELD_VALIDATOR.date, validation: dateValidator },
        { name: FIELD_VALIDATOR.dateLessThan, validation: dateLessThanValidator },
        { name: FIELD_VALIDATOR.dateLessThanOrEqualTo, validation: dateLessThanOrEqualToValidator },
        { name: FIELD_VALIDATOR.dateGreaterThan, validation: dateGreaterThanValidator },
        { name: FIELD_VALIDATOR.dateGreaterThanOrEqualTo, validation: dateGreaterThanOrEqualToValidator },
        { name: FIELD_VALIDATOR.numberGreaterThan, validation: numberGreaterThanValidator },
        { name: FIELD_VALIDATOR.numberLessThan, validation: numberLessThanValidator },
        { name: FIELD_VALIDATOR.verificationRequired, validation: verificationRequiredValidator },
        { name: FIELD_VALIDATOR.time, validation: timeValidator },
        { name: FIELD_VALIDATOR.url, validation: urlValidator },
        { name: FIELD_VALIDATOR.custom, validation: customValidator },
        { name: 'anyOneMandatory', validation: eitherMandatoryValidator },
        { name: 'mandatoryPartialDate', validation: eitherMandatoryValidator },
        { name: 'datagridRequired', validation: datagridRequiredValidator },
      ],
      extras: {
        resetFieldOnHide: true,
        checkExpressionOn: 'modelChange',
        lazyRender: true,
      },
    }),
  ],
  providers: [PendingChangesGuard],
  bootstrap: [AppComponent],
})
export class AppModule {
}